import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Check, X, Wand2, Star, ThumbsUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeoPage() {
    const { businessId } = useParams(); // Note: Admin usually passes businessId, or we use a context. 
    // For this demo, let's assume we are viewing the first business or passing it in URL.
    // We'll fetch the first business if ID is missing or handle errors.

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(null);

    // Fallback: If no businessId (e.g., accessed via admin sidebar without selection), 
    // we might need to list businesses or pick one. For now, let's hardcode a fetch 
    // or show a selector if needed. 
    // Simplification: We'll fetch a list and pick the first one if businessId is undefined.
    const [selectedBusinessId, setSelectedBusinessId] = useState(businessId);

    useEffect(() => {
        async function loadData() {
            try {
                let bid = selectedBusinessId;
                if (!bid) {
                    const res = await axios.get(`${API}/businesses?limit=1`, { withCredentials: true });
                    if (res.data.length > 0) {
                        bid = res.data[0].business_id;
                        setSelectedBusinessId(bid);
                    } else {
                        setLoading(false);
                        return; // No businesses
                    }
                }

                if (bid) {
                    const res = await axios.get(`${API}/seo/status/${bid}`, { withCredentials: true });
                    setStatus(res.data);
                }
            } catch (error) {
                console.error("SEO Load Error", error);
                toast.error("Nie udało się pobrać statusu SEO");
            } finally {
                setLoading(false);
            }
        }
        loadData();
    }, [selectedBusinessId]);

    const handleAction = async (actionId, actionData) => {
        setProcessing(actionId);
        try {
            await axios.post(`${API}/seo/action/${selectedBusinessId}`, {
                action_id: actionId,
                data: actionData
            }, { withCredentials: true });

            toast.success("Akcja wykonana pomyślnie!");
            // Remove the action from the list locally
            setStatus(prev => ({
                ...prev,
                opportunities: prev.opportunities.filter(o => o.id !== actionId),
                score: Math.min(100, prev.score + 5) // Optimistic update
            }));
        } catch (error) {
            toast.error("Błąd wykonania akcji");
        } finally {
            setProcessing(null);
        }
    };

    if (loading) return <div className="p-8 text-center">Ładowanie autopilota...</div>;
    if (!selectedBusinessId) return <div className="p-8 text-center text-slate-500">Brak firm w bazie. Dodaj firmę, aby użyć SEO Autopilota.</div>;

    return (
        <div className="max-w-5xl mx-auto space-y-8">
            <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <span className="text-green-600">🚀</span> Local SEO Autopilot
                </h1>
                <p className="text-slate-500">Twój asystent widoczności w Google Maps.</p>
            </div>

            {status?.status === 'disconnected' ? (
                <Card className="bg-orange-50 border-orange-200">
                    <CardContent className="p-8 text-center">
                        <h2 className="text-xl font-bold text-orange-800 mb-2">Brak połączenia z Google</h2>
                        <p className="text-orange-700 mb-6">Wymagane jest połączenie konta Google Business Profile, aby włączyć autopilota.</p>
                        <Button className="bg-blue-600 hover:bg-blue-700 text-white">Połącz konto Google</Button>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Health Score */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Card className="bg-slate-900 text-white col-span-1">
                            <CardHeader>
                                <CardTitle className="text-lg text-slate-300">SEO Health Score</CardTitle>
                            </CardHeader>
                            <CardContent className="flex items-end gap-2">
                                <span className="text-6xl font-black text-amber-500">{status?.score}</span>
                                <span className="text-xl text-slate-400 mb-2">/ 100</span>
                            </CardContent>
                        </Card>

                        <Card className="col-span-2">
                            <CardHeader>
                                <CardTitle>Twój plan na dziś</CardTitle>
                                <CardDescription>Wykonaj te misje, aby poprawić widoczność firmy.</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {status?.opportunities?.length === 0 ? (
                                    <div className="text-green-600 flex items-center gap-2">
                                        <Check className="w-5 h-5" /> Wszystko aktualne! Dobra robota.
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {status?.opportunities?.map(op => (
                                            <div key={op.id} className="border rounded-lg p-4 bg-white shadow-sm flex flex-col md:flex-row gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">Misja</Badge>
                                                        <h3 className="font-semibold">{op.title}</h3>
                                                    </div>

                                                    {op.type === 'review_reply' && (
                                                        <div className="bg-slate-50 p-3 rounded text-sm text-slate-600 mb-2">
                                                            <p className="italic">"{op.data.review.comment}"</p>
                                                            <div className="mt-2 flex gap-1">
                                                                {[...Array(5)].map((_, i) => (
                                                                    <Star key={i} className={`w-3 h-3 ${i < ["ONE", "TWO", "THREE", "FOUR", "FIVE"].indexOf(op.data.review.starRating) + 1 ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {op.type === 'review_reply' && (
                                                        <div className="relative">
                                                            <div className="absolute top-2 right-2 text-xs text-purple-600 flex items-center gap-1">
                                                                <Wand2 className="w-3 h-3" /> Propozycja AI
                                                            </div>
                                                            <textarea
                                                                className="w-full text-sm border rounded p-2 pt-6 min-h-[80px]"
                                                                defaultValue={op.data.draft_reply}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="flex flex-row md:flex-col gap-2 justify-center min-w-[120px]">
                                                    <Button
                                                        size="sm"
                                                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                                                        onClick={() => handleAction(op.id, { review_id: op.data.review.reviewId, reply_text: op.data.draft_reply })} // Using draft as is for now
                                                        disabled={processing === op.id}
                                                    >
                                                        {processing === op.id ? "Wysyłanie..." : "Zatwierdź"}
                                                    </Button>
                                                    <Button size="sm" variant="outline" className="w-full text-slate-500">
                                                        Edytuj
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </>
            )}
        </div>
    );
}
